import L from 'leaflet';
import proj4 from 'proj4';
import { toWgs84 } from 'reproject';
import { getAppSettings } from '../../Util/globalAppSettings';
import { ClusteredMarkerLayer } from '../Layers/ClusteredMarkersLayer';
import type { ClusteredMarkerLayerOptions } from '../Layers/Layers.types';
import Situation from '../PopupTemplates/Situation';
import { SituationSource } from '../Sources/SituationSource';
import { affectedArea } from './AffectedArea';
import { closedRoad } from './ClosedRoad';

//Situations at or above this severity with RoadClosed true will get a permanent closed-road layer.
const CLOSED_ROAD_MIN_SEVERITY = 5;

export const roadSituation = function (region: string, geoPageId?: number, options?: ClusteredMarkerLayerOptions): ClusteredMarkerLayer {
    const defaultOptions: ClusteredMarkerLayerOptions = {
        name: 'Vägtrafik',
        itemPlural: 'störningar',
        popupTemplate: Situation,
        source: new SituationSource(region, 'Vägtrafik', geoPageId, {
            updateInterval: 60,
        }),
        markerPositions(feature) {
            if (!feature.properties.sweRef99Extent || feature.properties.sweRef99Extent.type !== 'LineString') {
                return null;
            }
            const wgs84Extent = toWgs84(
                feature.properties.sweRef99Extent,
                '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
                proj4,
            );

            const extentStart = L.GeoJSON.coordsToLatLng(wgs84Extent.coordinates[0]);
            if (feature.properties.affectedDirection !== 'Both') {
                return [extentStart];
            }

            const extentEnd = L.GeoJSON.coordsToLatLng(wgs84Extent.coordinates[wgs84Extent.coordinates.length - 1]);

            return [extentStart, extentEnd];
        },
        splitMarkersMinDistance: 400,
        createInformationLayer: function (feature) {
            const layers = [];
            if (feature.properties.sweRef99Extent) {
                layers.push(
                    affectedArea(
                        toWgs84(feature.properties.sweRef99Extent, '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs', proj4),
                        { arrowDirection: feature.properties.affectedDirection.toLowerCase() },
                    ),
                );
            }
            if (feature.properties.sweRef99Area) {
                layers.push(
                    affectedArea(
                        toWgs84(feature.properties.sweRef99Area, '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs', proj4),
                    ),
                );
            }
            return layers.length > 0 ? L.layerGroup(layers) : null;
        },
        createPersistentOverlay: function (feature) {
            const { roadClosed, severity, sweRef99Extent } = feature.properties;
            if (
                !getAppSettings().enableClosedRoadLayer ||
                !roadClosed ||
                severity < CLOSED_ROAD_MIN_SEVERITY ||
                !sweRef99Extent?.type?.includes('Line')
            ) {
                return null;
            }
            return closedRoad(feature);
        },
        persistentInformationLayer: false,
        onMarkerAdd: function (marker, feature) {
            marker.getElement().setAttribute('data-situation-id', feature.properties.id);
        },
    };

    return new ClusteredMarkerLayer({ ...defaultOptions, ...options });
};
